/**
 * One delivery channel — "Email (SMTP)" or "Message API Server" — as an editable card:
 * enable switch, connection fields, write-only secret, Save, and Send test.
 *
 * Used on a site's Alert Actions page (perspective 'site') and on the cloud's Central Alert
 * Actions page (perspective 'central', which adds "Always also notify" recipients).
 *
 * The secret (SMTP password / client key) is never shown: the field is blank with a "saved"
 * hint, typing replaces it, leaving it blank keeps it. "Send test" tests what is in the
 * form — unsaved changes included — so a change can be checked before it is saved.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { Callout, StatusPill } from '../common/Callout';
import { AACard, AASwitch, AASegmented, aaInputCls, aaLabelCls } from './AlertActionsControls';
import {
  CHANNEL_TITLE,
  GMAIL_CALLBACK_PATH,
  alertActionsErrorText,
  type AlertChannelKey,
  type AlertChannelState,
  type AlertChannelsApi,
} from './alertActionsModel';

export interface DeliveryChannelCardProps {
  channel: AlertChannelKey;
  state: AlertChannelState | null;
  api: AlertChannelsApi;
  perspective: 'site' | 'central';
  canEdit: boolean;
  onSaved: (next: AlertChannelState) => void;
}

const EMAIL_SECURITY = [
  { value: 'starttls', label: 'STARTTLS (587 / 25)' },
  { value: 'ssl', label: 'SSL/TLS (465)' },
];

function settingsForm(channel: AlertChannelKey, s: Record<string, any> = {}): Record<string, string> {
  if (channel === 'email') {
    return {
      provider: s.provider === 'gmail_api' ? 'gmail_api' : 'smtp',
      gmail_client_id: s.gmail_client_id || '',
      host: s.host || '',
      port: String(s.port ?? 587),
      security: s.secure ? 'ssl' : 'starttls',
      username: s.username || '',
      from_email: s.from_email || '',
      from_name: s.from_name || '',
      extra_recipients: (s.extra_recipients || []).join(', '),
    };
  }
  return {
    base_url: s.base_url || '',
    default_country_code: s.default_country_code || '',
    rate_limit_per_minute: String(s.rate_limit_per_minute ?? 10),
    extra_recipients: (s.extra_recipients || []).join(', '),
  };
}

function formToSettings(channel: AlertChannelKey, f: Record<string, string>, perspective: 'site' | 'central') {
  const extra = perspective === 'central' ? { extra_recipients: f.extra_recipients } : {};
  if (channel === 'email') {
    return {
      provider: f.provider,
      gmail_client_id: f.gmail_client_id.trim(),
      host: f.host.trim(),
      port: parseInt(f.port, 10) || 587,
      secure: f.security === 'ssl',
      username: f.username.trim(),
      from_email: f.from_email.trim(),
      from_name: f.from_name.trim(),
      ...extra,
    };
  }
  return {
    base_url: f.base_url.trim(),
    default_country_code: f.default_country_code.trim(),
    rate_limit_per_minute: parseInt(f.rate_limit_per_minute, 10) || 10,
    ...extra,
  };
}

function statusOf(state: AlertChannelState | null) {
  if (!state || !state.configured) return { tone: 'neutral' as const, text: 'Not configured' };
  if (!state.enabled) return { tone: 'neutral' as const, text: 'Off' };
  if (state.last_test_ok === false) return { tone: 'warning' as const, text: 'On · last test failed' };
  if (state.last_test_ok === true) return { tone: 'success' as const, text: 'On · tested' };
  return { tone: 'info' as const, text: 'On · not tested' };
}

export default function DeliveryChannelCard({ channel, state, api, perspective, canEdit, onSaved }: DeliveryChannelCardProps) {
  const saved = useMemo(() => settingsForm(channel, state?.settings), [channel, state]);
  const [form, setForm] = useState<Record<string, string>>(saved);
  const [enabled, setEnabled] = useState(!!state?.enabled);
  const [secret, setSecret] = useState('');
  const [gmailSecret, setGmailSecret] = useState('');
  const [copied, setCopied] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState<'save' | 'test' | 'connect' | null>(null);
  const [result, setResult] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  useEffect(() => {
    setForm(saved);
    setEnabled(!!state?.enabled);
    setSecret('');
    setGmailSecret('');
  }, [saved, state]);

  const settingsDirty = JSON.stringify(form) !== JSON.stringify(saved);
  const dirty = settingsDirty || !!secret || !!gmailSecret || enabled !== !!state?.enabled;
  const gmailAvailable = channel === 'email' && !!api.gmailConnect;
  const isGmail = gmailAvailable && form.provider === 'gmail_api';
  const redirectUri = typeof window === 'undefined' ? GMAIL_CALLBACK_PATH : `${window.location.origin}${GMAIL_CALLBACK_PATH}`;
  const set = (k: string) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const disabled = !canEdit || busy !== null;

  const saveBody = (nextEnabled: boolean) => ({
    enabled: nextEnabled,
    settings: formToSettings(channel, form, perspective),
    secret: secret || undefined,
    gmail_client_secret: gmailSecret || undefined,
  });

  async function save(nextEnabled = enabled) {
    setBusy('save');
    setResult(null);
    try {
      const next = await api.saveChannel(channel, saveBody(nextEnabled));
      onSaved(next);
      setResult({ tone: 'success', text: `${CHANNEL_TITLE[channel]} saved.` });
    } catch (err) {
      setEnabled(!!state?.enabled);
      setResult({ tone: 'danger', text: alertActionsErrorText(err, 'Could not save') });
    } finally {
      setBusy(null);
    }
  }

  /** Save what is in the form (Client ID / secret), then hand the browser to Google's consent screen. */
  async function connectGoogle() {
    if (!api.gmailConnect) return;
    setBusy('connect');
    setResult(null);
    try {
      if (dirty) {
        // Keep the channel on only if it already works — a first connection cannot be "on" yet.
        const next = await api.saveChannel(channel, saveBody(enabled && !!state?.gmail?.connected && !settingsDirty));
        onSaved(next);
      }
      const { auth_url } = await api.gmailConnect(redirectUri);
      window.location.assign(auth_url);
    } catch (err) {
      setResult({ tone: 'danger', text: alertActionsErrorText(err, 'Could not start the Google sign-in') });
      setBusy(null);
    }
  }

  async function disconnectGoogle() {
    setBusy('save');
    setResult(null);
    try {
      const next = await api.saveChannel(channel, { enabled: false, disconnect_gmail: true });
      onSaved(next);
      setResult({ tone: 'success', text: 'Google account disconnected.' });
    } catch (err) {
      setResult({ tone: 'danger', text: alertActionsErrorText(err, 'Could not disconnect') });
    } finally {
      setBusy(null);
    }
  }

  function copyRedirect() {
    void navigator.clipboard?.writeText(redirectUri).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  async function test() {
    setBusy('test');
    setResult(null);
    try {
      const r = await api.testChannel(channel, {
        to: testTo,
        settings: settingsDirty ? formToSettings(channel, form, perspective) : undefined,
        secret: secret || undefined,
        gmail_client_secret: gmailSecret || undefined,
      });
      setResult({ tone: r.ok ? 'success' : 'danger', text: r.message });
    } catch (err) {
      setResult({ tone: 'danger', text: alertActionsErrorText(err, 'Test failed') });
    } finally {
      setBusy(null);
    }
  }

  const status = statusOf(state);
  const isEmail = channel === 'email';
  const secretLabel = isEmail ? 'Password' : 'Client key';
  const secretHint = state?.has_secret ? '•••••••• saved — leave blank to keep' : isEmail ? 'Leave blank if the server needs none' : 'X-Client-Key from your provider';

  const subtitle = isGmail
    ? 'Alert emails sent through the Gmail API over HTTPS — works where the host blocks SMTP ports (e.g. DigitalOcean).'
    : isEmail
    ? perspective === 'site'
      ? 'The mail server this site sends alert emails through.'
      : 'The mail server the cloud sends alert emails through, for every site that relays to it.'
    : perspective === 'site'
      ? 'WhatsApp messages through your Message API Server, sent from this site.'
      : 'WhatsApp messages through your Message API Server, sent by the cloud for every site that relays to it.';

  return (
    <AACard
      icon={isEmail ? EnvelopeIcon : ChatBubbleLeftRightIcon}
      title={isGmail ? 'Email (Gmail API)' : CHANNEL_TITLE[channel]}
      subtitle={subtitle}
      right={
        <>
          <StatusPill tone={status.tone}>{status.text}</StatusPill>
          <AASwitch
            checked={enabled}
            disabled={!canEdit}
            busy={busy === 'save'}
            label={enabled ? `Turn ${CHANNEL_TITLE[channel]} off` : `Turn ${CHANNEL_TITLE[channel]} on`}
            onChange={(next) => {
              setEnabled(next);
              // Turning OFF needs nothing else — save it straight away. Turning ON saves too
              // when the form is otherwise unchanged and already complete.
              if (!next || (state?.configured && !settingsDirty && !secret)) void save(next);
            }}
          />
        </>
      }
      footer={
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              disabled={disabled}
              className={`${aaInputCls} mt-0 min-w-[12rem] flex-1`}
              placeholder={isEmail ? 'Send test to you@example.com (blank = connection check)' : 'Send test to 9198xxxxxxxx (blank = key check)'}
            />
            <button
              type="button"
              onClick={test}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[13px] text-foreground hover:bg-muted disabled:opacity-50"
            >
              {busy === 'test'
                ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
                : <PaperAirplaneIcon className="h-4 w-4" />}
              {testTo.trim() ? 'Send test' : 'Check connection'}
            </button>
            <button
              type="button"
              onClick={() => save()}
              disabled={disabled || !dirty}
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {busy === 'save' && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />}
              Save
            </button>
          </div>
          {result && <Callout tone={result.tone}>{result.text}</Callout>}
          {!result && state?.last_test_at && (
            <p className="text-[11px] text-muted-foreground">
              Last test {new Date(state.last_test_at).toLocaleString()} — {state.last_test_ok ? 'passed' : 'failed'}
              {state.last_test_message ? `: ${state.last_test_message}` : ''}
            </p>
          )}
        </div>
      }
    >
      {gmailAvailable && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className={aaLabelCls}>Send with</span>
          <AASegmented<string>
            value={form.provider}
            disabled={disabled}
            onChange={(v) => setForm((f) => ({ ...f, provider: v }))}
            options={[
              { value: 'smtp', label: 'SMTP server' },
              { value: 'gmail_api', label: 'Gmail API (Google Cloud)', hint: 'HTTPS — for hosts that block SMTP ports' },
            ]}
          />
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
        {isGmail ? (
          <>
            <label className="block sm:col-span-6">
              <span className={aaLabelCls}>OAuth Client ID</span>
              <input value={form.gmail_client_id} onChange={set('gmail_client_id')} disabled={disabled} className={aaInputCls} placeholder="1234567890-abc….apps.googleusercontent.com" autoComplete="off" />
            </label>
            <label className="block sm:col-span-6">
              <span className={aaLabelCls}>OAuth Client secret</span>
              <input
                type="password"
                value={gmailSecret}
                onChange={(e) => setGmailSecret(e.target.value)}
                disabled={disabled}
                className={aaInputCls}
                placeholder={state?.gmail?.has_client_secret ? '•••••••• saved — leave blank to keep' : 'GOCSPX-…'}
                autoComplete="new-password"
              />
            </label>
            <div className="block sm:col-span-6">
              <span className={aaLabelCls}>Authorized redirect URI (add this to the OAuth client in Google Cloud Console)</span>
              <div className="mt-0.5 flex gap-1.5">
                <input readOnly value={redirectUri} className={`${aaInputCls} mt-0 font-mono text-[12px]`} onFocus={(e) => e.target.select()} />
                <button type="button" onClick={copyRedirect} className="shrink-0 rounded-lg border border-border px-2.5 text-muted-foreground hover:bg-muted" title="Copy">
                  {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border px-3 py-2 sm:col-span-6">
              <span className="text-[12px] text-muted-foreground">Google account</span>
              {state?.gmail?.connected
                ? <StatusPill tone="success">Connected{state.gmail.account ? ` as ${state.gmail.account}` : ''}</StatusPill>
                : <StatusPill tone="neutral">Not connected</StatusPill>}
              <span className="flex-1" />
              {state?.gmail?.connected && (
                <button type="button" onClick={disconnectGoogle} disabled={disabled} className="rounded-lg px-2.5 py-1 text-[12px] text-muted-foreground hover:bg-muted disabled:opacity-50">
                  Disconnect
                </button>
              )}
              <button
                type="button"
                onClick={connectGoogle}
                disabled={disabled || !form.gmail_client_id.trim() || !(gmailSecret || state?.gmail?.has_client_secret)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-[12px] font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {busy === 'connect' && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />}
                {state?.gmail?.connected ? 'Reconnect Google account' : 'Connect Google account'}
              </button>
            </div>
            <label className="block sm:col-span-3">
              <span className={aaLabelCls}>From address (optional)</span>
              <input value={form.from_email} onChange={set('from_email')} disabled={disabled} className={aaInputCls} placeholder={state?.gmail?.account || 'the connected account'} />
            </label>
            <label className="block sm:col-span-3">
              <span className={aaLabelCls}>From name</span>
              <input value={form.from_name} onChange={set('from_name')} disabled={disabled} className={aaInputCls} placeholder="TRINETRA Cloud Alerts" />
            </label>
            <details className="rounded-lg border border-border px-3 py-2 text-[12px] text-muted-foreground sm:col-span-6">
              <summary className="cursor-pointer font-medium text-foreground">How to get the Client ID and secret (Google Cloud Console)</summary>
              <ol className="mt-2 list-decimal space-y-1 pl-5">
                <li>console.cloud.google.com → create or pick a project.</li>
                <li>APIs &amp; Services → Library → <span className="text-foreground">Gmail API</span> → Enable.</li>
                <li>OAuth consent screen: on Google Workspace choose <span className="text-foreground">Internal</span>. Otherwise External, add yourself as a test user, then <span className="text-foreground">Publish app</span> — in &quot;Testing&quot; Google ends the connection after 7 days.</li>
                <li>Credentials → Create credentials → OAuth client ID → <span className="text-foreground">Web application</span>; under Authorized redirect URIs paste the URI above.</li>
                <li>Copy the Client ID and Client secret here, then <span className="text-foreground">Connect Google account</span> and sign in with the mailbox that should send the alerts.</li>
                <li>Back here: turn the channel on and send a test.</li>
              </ol>
              <p className="mt-2">Permission asked: send email only — the cloud cannot read the mailbox.</p>
            </details>
          </>
        ) : isEmail ? (
          <>
            <label className="block sm:col-span-4">
              <span className={aaLabelCls}>SMTP server</span>
              <input value={form.host} onChange={set('host')} disabled={disabled} className={aaInputCls} placeholder="smtp.gmail.com" />
            </label>
            <label className="block sm:col-span-2">
              <span className={aaLabelCls}>Port</span>
              <input type="number" min={1} max={65535} value={form.port} onChange={set('port')} disabled={disabled} className={aaInputCls} />
            </label>
            <label className="block sm:col-span-2">
              <span className={aaLabelCls}>Security</span>
              <select value={form.security} onChange={set('security')} disabled={disabled} className={aaInputCls}>
                {EMAIL_SECURITY.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className={aaLabelCls}>Username</span>
              <input value={form.username} onChange={set('username')} disabled={disabled} className={aaInputCls} placeholder="alerts@company.com" autoComplete="off" />
            </label>
            <label className="block sm:col-span-2">
              <span className={aaLabelCls}>{secretLabel}</span>
              <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} disabled={disabled} className={aaInputCls} placeholder={secretHint} autoComplete="new-password" />
            </label>
            <label className="block sm:col-span-3">
              <span className={aaLabelCls}>From address</span>
              <input value={form.from_email} onChange={set('from_email')} disabled={disabled} className={aaInputCls} placeholder="alerts@company.com" />
            </label>
            <label className="block sm:col-span-3">
              <span className={aaLabelCls}>From name</span>
              <input value={form.from_name} onChange={set('from_name')} disabled={disabled} className={aaInputCls} placeholder={perspective === 'site' ? 'TRINETRA Alerts' : 'TRINETRA Cloud Alerts'} />
            </label>
          </>
        ) : (
          <>
            <label className="block sm:col-span-6">
              <span className={aaLabelCls}>Server URL</span>
              <input value={form.base_url} onChange={set('base_url')} disabled={disabled} className={aaInputCls} placeholder="https://msg.example.com" />
            </label>
            <label className="block sm:col-span-6">
              <span className={aaLabelCls}>{secretLabel}</span>
              <input type="password" value={secret} onChange={(e) => setSecret(e.target.value)} disabled={disabled} className={aaInputCls} placeholder={secretHint} autoComplete="new-password" />
            </label>
            <label className="block sm:col-span-3">
              <span className={aaLabelCls}>Default country code</span>
              <input value={form.default_country_code} onChange={set('default_country_code')} disabled={disabled} className={aaInputCls} placeholder="91" />
            </label>
            <label className="block sm:col-span-3">
              <span className={aaLabelCls}>Max messages / minute</span>
              <input type="number" min={1} max={600} value={form.rate_limit_per_minute} onChange={set('rate_limit_per_minute')} disabled={disabled} className={aaInputCls} />
            </label>
          </>
        )}
        {perspective === 'central' && (
          <label className="block sm:col-span-6">
            <span className={aaLabelCls}>Always also notify (every relayed alert that uses this channel)</span>
            <input
              value={form.extra_recipients}
              onChange={set('extra_recipients')}
              disabled={disabled}
              className={aaInputCls}
              placeholder={isEmail ? 'control-room@company.com, ops@company.com' : '919876543210, 120363431346189013@g.us'}
            />
          </label>
        )}
      </div>
      {!isEmail && (
        <p className="mt-2 text-[11px] text-muted-foreground">
          Recipients are numbers with country code (<span className="font-mono">919876543210</span>) or WhatsApp group ids
          (<span className="font-mono">120363…@g.us</span>). The server allows about 10 messages a minute; extra messages wait their turn.
        </p>
      )}
    </AACard>
  );
}
